<template>
  <LoadingBlock :loading="loading">
    <form @submit.prevent="submit">
      <div class="mb-3">
        <label class="form-label" :for="textareaId">Ссылки — по одной на строку</label>
        <textarea :id="textareaId" class="form-control links-textarea" rows="8" v-model="linksText"
                  placeholder="https://pub.fsa.gov.ru/rds/declaration/view/...&#10;https://.../certificate.pdf"
                  @input="scheduleCheck"></textarea>
        <div class="d-flex justify-content-between align-items-center mt-1 small">
          <span :class="tooManyLinks ? 'text-danger' : 'text-muted'">
            Ссылок: {{ links.length }} из {{ maxLinks }}
            <template v-if="tooManyLinks"> — уберите лишние, за раз можно не больше {{ maxLinks }}</template>
          </span>
          <a href="#" v-if="links.length" @click.prevent="runCheck">Проверить</a>
        </div>
      </div>

      <div v-if="checkLoading || checkResults.length || checkError" class="mb-3">
        <div v-if="checkLoading" class="text-muted small">Проверяем ссылки…</div>
        <div v-else-if="checkError" class="text-danger small">{{ checkError }}</div>
        <template v-else>
          <div class="small mb-2">
            Будет загружено: <b>{{ supportedCount }}</b> из {{ checkResults.length }}
          </div>
          <ul class="list-unstyled links-preview mb-0">
            <li v-for="(item, index) in checkResults" :key="index" class="d-flex align-items-start gap-2 mb-1">
              <span class="badge flex-shrink-0" :class="'bg-label-' + linkView(item).color">
                {{ linkView(item).label }}
              </span>
              <span class="text-break small text-muted">{{ item.url }}</span>
            </li>
          </ul>
        </template>
      </div>

      <div v-if="showProductSelect" class="mb-3">
        <label class="form-label">Сразу привязать к товарам (необязательно)</label>
        <MultiSelect
            v-model="selectedProductIds"
            :options="productOptions"
            optionLabel="label"
            optionValue="id"
            filter
            placeholder="Выберите товары"
            style="width: 100%"
            @filter="onProductFilter"
            @change="rememberSelectedProducts"
        />
        <div class="form-text">
          Если товары не выбраны, сертификаты просто создадутся, а к товарам их привяжет «Сопоставление с товарами».
        </div>
      </div>

      <div class="text-center mt-4">
        <button type="submit" class="btn btn-primary" :disabled="submitDisabled">
          Загрузить
        </button>
      </div>
      <p v-if="loading && waitingTask" class="text-center text-muted mt-2 mb-0">Загрузка запущена, открываем задачу…</p>
    </form>
  </LoadingBlock>
</template>

<script>
import LoadingBlock from "@/components/page/LoadingBlock.vue";
import {MultiSelect} from "primevue";
import {checkLinks, createFromLinks} from "@/api/certificate";
import {getProducts} from "@/api/product";
import {user} from "@/data/user";
import {Message, Messages} from "@/data/messages";
import {
    CERTIFICATE_LINKS_DOWNLOAD_TASK,
    getLinkCheckView,
    MAX_CERTIFICATE_LINKS,
    parseRequestError,
    splitLinks,
} from "@/data/certificate";
import {getLatestTaskId, openStartedTask} from "@/helpers/task";

const CHECK_DEBOUNCE = 500

let instanceCounter = 0

export default {
  name: "CertificateFromLinksForm",
  components: {LoadingBlock, MultiSelect},
  props: {
    // Товары, к которым привязать сертификаты. Если заданы — выбор товаров не показывается
    productIds: {
      type: Array,
      default: null,
    },
    // Переходить к задаче после запуска
    navigateToTask: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['started'],
  data() {
    instanceCounter += 1
    return {
      textareaId: `certificate-links-${instanceCounter}`,
      linksText: '',
      maxLinks: MAX_CERTIFICATE_LINKS,
      loading: false,
      waitingTask: false,
      checkLoading: false,
      checkResults: [],
      checkError: '',
      checkTimer: null,
      checkSeq: 0,
      checkedText: null,
      selectedProductIds: [],
      selectedProductsCache: {},
      productOptions: [],
      productFilterTimer: null,
    }
  },
  computed: {
    links() {
      return splitLinks(this.linksText)
    },
    tooManyLinks() {
      return this.links.length > MAX_CERTIFICATE_LINKS
    },
    showProductSelect() {
      return !this.productIds
    },
    supportedCount() {
      return this.checkResults.filter((item) => item.supported).length
    },
    checkIsActual() {
      return this.checkedText === this.linksText && !this.checkLoading && !this.checkError
    },
    allUnsupported() {
      return this.checkIsActual && this.checkResults.length > 0 && this.supportedCount === 0
    },
    submitDisabled() {
      return this.loading || !this.links.length || this.tooManyLinks || this.allUnsupported
    },
  },
  methods: {
    linkView(item) {
      return getLinkCheckView(item)
    },
    reset() {
      clearTimeout(this.checkTimer)
      this.checkSeq += 1
      this.linksText = ''
      this.checkResults = []
      this.checkError = ''
      this.checkedText = null
      this.checkLoading = false
      this.selectedProductIds = []
      this.loading = false
      this.waitingTask = false
    },
    scheduleCheck() {
      clearTimeout(this.checkTimer)
      this.checkTimer = setTimeout(() => this.runCheck(), CHECK_DEBOUNCE)
    },
    async runCheck() {
      clearTimeout(this.checkTimer)
      const text = this.linksText
      const seq = ++this.checkSeq
      if (!this.links.length) {
        this.checkResults = []
        this.checkError = ''
        this.checkedText = text
        this.checkLoading = false
        return
      }
      this.checkLoading = true
      this.checkError = ''
      try {
        const result = await checkLinks(this.links)
        if (seq !== this.checkSeq) {
          return
        }
        this.checkResults = Array.isArray(result.data) ? result.data : []
        this.checkedText = text
      } catch (e) {
        if (seq !== this.checkSeq) {
          return
        }
        this.checkResults = []
        this.checkError = parseRequestError(e, 'Не удалось проверить ссылки')
      } finally {
        if (seq === this.checkSeq) {
          this.checkLoading = false
        }
      }
    },
    onProductFilter(event) {
      clearTimeout(this.productFilterTimer)
      this.productFilterTimer = setTimeout(() => this.searchProducts(event.value), 300)
    },
    rememberSelectedProducts() {
      const options = Object.fromEntries(this.productOptions.map((option) => [option.id, option]))
      const cache = {}
      this.selectedProductIds.forEach((id) => {
        const option = options[id] || this.selectedProductsCache[id]
        if (option) {
          cache[id] = option
        }
      })
      this.selectedProductsCache = cache
    },
    async searchProducts(name = '') {
      const query = {size: 50}
      if (name) {
        query.name = name
      }
      try {
        const result = await getProducts(await user.getActiveCompany(), query)
        const found = (result.data.items || []).map((product) => ({
          id: product.id,
          label: product.name,
        }))
        // выбранные товары оставляем в списке, иначе они пропадут из поля после нового поиска
        const foundIds = new Set(found.map((option) => option.id))
        const selected = Object.values(this.selectedProductsCache).filter((option) => !foundIds.has(option.id))
        this.productOptions = [...selected, ...found]
      } catch (e) {
        // список товаров для выбора — необязательная часть формы
      }
    },
    async submit() {
      if (this.submitDisabled) {
        return
      }
      this.loading = true
      const message = new Message()
      try {
        const company_id = await user.getActiveCompany()
        const previousTaskId = this.navigateToTask
            ? await getLatestTaskId(company_id, CERTIFICATE_LINKS_DOWNLOAD_TASK)
            : null
        const productIds = this.productIds || this.selectedProductIds
        const result = await createFromLinks(company_id, this.links, productIds)
        if (result.status === 202 || result.status === 200 || result.status === 201) {
          message.type = 'success'
          message.autoremove = 10
          message.text = 'Загрузка запущена, результат — в задачах'
          Messages.list.push(message)
          this.$emit('started')
          if (this.navigateToTask) {
            this.waitingTask = true
            await openStartedTask(this.$router, company_id, CERTIFICATE_LINKS_DOWNLOAD_TASK, previousTaskId)
          }
          this.reset()
        } else {
          message.type = 'failure'
          message.text = 'Не удалось запустить загрузку'
          Messages.list.push(message)
        }
      } catch (e) {
        message.type = 'failure'
        message.text = parseRequestError(e, 'Не удалось запустить загрузку')
        Messages.list.push(message)
      }
      this.loading = false
      this.waitingTask = false
    },
  },
  async mounted() {
    if (this.showProductSelect) {
      await this.searchProducts()
    }
  },
  beforeUnmount() {
    clearTimeout(this.checkTimer)
    clearTimeout(this.productFilterTimer)
  },
}
</script>

<style scoped>
.links-textarea {
  font-family: monospace;
  font-size: 0.85rem;
}

.links-preview {
  max-height: 240px;
  overflow-y: auto;
}
</style>
